import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  stripUrl,
  serializeAccessibleInventories,
} from '@marketmuse/utilities';

import { useSuiteNavigate } from '../../../hooks/useSuiteNavigate';
import useSiteChange from '../../../hooks/useSiteChange';
import useLogout from '../../../hooks/auth/useLogout';
import useGhostMode from '../../../hooks/auth/useGhostMode';
import useIdentitySuite from '../../../hooks/auth/useIdentitySuite';

import getSignInPathDefault from '../../../utils/getSignInPathDefault';
import isAdminMode, { isGhosting } from '../../../utils/isAdminMode';

import * as miscActions from '../../../actions/miscActions';
import * as adminActions from '../../../actions/adminActions';

import SimpleMenu from '../../SimpleMenu/SimpleMenu';
import MenuItemSeparator from '../../SimpleMenu/styles/MenuItemSeparator';

const SimpleMenuUserProfile = ({
  origin,
  showSidebarCollapse,
  children,
  ...props
}) => {
  const navigate = useSuiteNavigate();
  const dispatch = useDispatch();

  const user = useSelector(state => state.user);
  const admin = useSelector(state => state.admin);
  const sites = useSelector(state => state.user?.org?.sites) || [];
  const developerMode = useSelector(state => state.persist?.developerMode);

  const { isSuperUser, orgId, orgSlug, siteId, userId } = useIdentitySuite();
  const { disableGhostMode } = useGhostMode();
  const handleLogout = useLogout();
  const setSite = useSiteChange({
    siteId,
    slug: orgSlug,
    resetApps: true,
    skipFetchPermissions: false,
  });

  const isAdmin = isAdminMode(admin, user);
  const isGhost = isGhosting(admin, user);
  const isDeveloper = isAdmin && developerMode;

  const inventories = useMemo(
    () => serializeAccessibleInventories(sites),
    [sites],
  );

  const items = [
    ...(inventories.length > 1
      ? [
          {
            title: 'Inventories',
            items: inventories.map(site => ({
              title: stripUrl(site.title),
              key: site.id,
              onClick: () => {
                const path = getSignInPathDefault();

                setSite({ siteId: site.id, slug: orgSlug, path });
              },
            })),
          },
          {
            component: MenuItemSeparator,
          },
        ]
      : []),
    {
      title: 'Settings',
      onClick: e => {
        navigate('settings/account', { omitSiteId: true });
      },
    },
    ...(isSuperUser
      ? [
          {
            title: `${isAdmin ? 'Disable' : 'Enable'} Admin Mode`,
            onClick: e => {
              dispatch(
                miscActions.setSimple({
                  key: 'developerMode',
                  value: false,
                  persist: true,
                }),
              );
              if (isAdmin) {
                dispatch(adminActions.disableAdminMode());
              } else {
                dispatch(adminActions.adminModeEnable({ user: user }));
              }
            },
          },

          ...(isAdmin
            ? [
                {
                  title: `${isDeveloper ? 'Disable' : 'Enable'} Developer Mode`,
                  onClick: e => {
                    dispatch(
                      miscActions.setSimple({
                        key: 'developerMode',
                        value: !isDeveloper,
                        persist: true,
                      }),
                    );
                  },
                },
                {
                  title: 'Organization Card',
                  onClick: () => {
                    navigate(`admin/orgs/${orgId}`, {
                      omitSiteId: true,
                      omitOrgSlug: true,
                    });
                  },
                },
              ]
            : []),
        ]
      : []),

    ...(isGhost
      ? [
          {
            component: MenuItemSeparator,
          },
          {
            title: 'Organization Card',
            onClick: () => {
              disableGhostMode();
              navigate(`admin/orgs/${orgId}`, {
                omitSiteId: true,
                omitOrgSlug: true,
              });
            },
          },
          {
            title: 'Switch back to your account',
            onClick: e => {
              disableGhostMode();
            },
          },
        ]
      : []),

    {
      component: MenuItemSeparator,
    },
    {
      title: 'Sign Out',
      onClick: () => {
        handleLogout();
      },
    },
  ].filter(Boolean);

  const unAuthorizedMenuItems = [
    {
      title: 'Login',
      onClick: () => {
        handleLogout();
      },
    },
  ];

  return (
    <SimpleMenu
      label="User menu"
      items={userId ? items : unAuthorizedMenuItems}
      origin={origin}
      {...props}
    >
      {children}
    </SimpleMenu>
  );
};

SimpleMenuUserProfile.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'right',
    'right-start',
    'right-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'left-start',
    'left-end',
    'auto',
    'auto-start',
    'auto-end',
  ]),
  offset: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
};

export default SimpleMenuUserProfile;
